import { useCallback, type ComponentPropsWithoutRef } from "react";

import { cx } from "../cx";
import { useDragResize } from "./useDragResize";
import { useKeyboardResize } from "./useKeyboardResize";

interface FlexHandleProps extends Omit<
  ComponentPropsWithoutRef<"button">,
  "onResize" | "aria-label"
> {
  "aria-label": string;
  direction: 1 | -1;
  maxWidth?: number;
  minWidth: number;
  onResize: (width: number) => void;
  onResizeEnd?: () => void;
  onResizeStart?: () => void;
  width: number;
}

export const FlexHandle = ({
  width,
  direction,
  minWidth,
  maxWidth,
  onResize,
  onResizeStart,
  onResizeEnd,
  className,
  ...rest
}: FlexHandleProps) => {
  const clamp = useCallback(
    (value: number) =>
      Math.min(maxWidth ?? Infinity, Math.max(minWidth, value)),
    [minWidth, maxWidth],
  );

  const { handleRef, handlePointerDown } = useDragResize({
    width,
    direction,
    clamp,
    onResize,
    onResizeStart,
    onResizeEnd,
  });

  const { handleKeyDown } = useKeyboardResize({
    width,
    direction,
    clamp,
    minWidth,
    maxWidth,
    onResize,
  });

  return (
    <button
      ref={handleRef}
      type="button"
      className={cx(
        "group relative z-20 w-0 shrink-0",
        "cursor-col-resize touch-none outline-none",
        "before:absolute before:inset-y-0 before:left-0 before:-right-2.5 before:content-['']",
        className,
      )}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      <div
        className={cx(
          "pointer-events-none absolute right-0 top-1/2",
          "h-8 w-1 -translate-y-1/2 translate-x-1/2 rounded-full bg-transparent",
          "transition-[background-color,height,transform] duration-150 ease-out",
          "group-hover:h-12 group-hover:bg-[var(--color-icon-tertiary)]",
          "group-focus-visible:bg-[var(--color-icon-tertiary)]",
          "group-active:h-14 group-active:scale-y-105 group-active:bg-[var(--color-icon-secondary)]",
        )}
      />
    </button>
  );
};

FlexHandle.displayName = "Resizable.FlexHandle";
